import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  DialogTitle,
  FormControl,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { api } from "../../lib/api";
import { dealError } from "../../lib/proposalDealsApi";
import { TakeoffOrderDialog } from "../../components/measurements/TakeoffOrderDialog";

interface Handoff {
  job: { id: number; number: string } | null;
  needs_link?: boolean;
  needs_additional_scope?: boolean;
  change_order?: number | null;
  total: string | null;
  issue: string;
  can_confirm: boolean;
  destination: number | null;
  destinations: { id: number; label: string }[];
}

export function ProposalJobHandoff({
  endpoint,
  authorizedByStaff = false,
  materialsProposalId,
}: {
  endpoint: string;
  authorizedByStaff?: boolean;
  materialsProposalId?: number;
}) {
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [notificationDismissed, setNotificationDismissed] = useState(false);
  const close = () => {
    setOpen(false);
    setNotificationDismissed(true);
  };
  const showConfirmation =
    open || (params.get("handoff") === "1" && !notificationDismissed);
  const [destination, setDestination] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["proposal-job-handoff", endpoint],
    queryFn: async () => (await api.get<Handoff>(endpoint)).data,
  });
  const data = query.data;
  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      const result = (
        await api.post<Handoff>(endpoint, {
          destination: destination ?? data?.destination,
        })
      ).data;
      client.setQueryData(["proposal-job-handoff", endpoint], result);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["proposal-deal"] }),
        client.invalidateQueries({ queryKey: ["opportunities"] }),
        client.invalidateQueries({ queryKey: ["jobs"] }),
      ]);
      if (result.job)
        navigate(
          `/operations/jobs/${result.job.id}${result.change_order ? "?tab=change-orders" : ""}`,
        );
    } catch (e) {
      setError(dealError(e));
    } finally {
      setBusy(false);
    }
  };
  if (query.isError)
    return (
      <Alert
        color="warning"
        endDecorator={
          <Button onClick={() => void query.refetch()}>Retry</Button>
        }
      >
        Job setup could not load. The authorization record is saved.
      </Alert>
    );
  if (!data)
    return (
      <Card>
        <Typography>Loading job setup…</Typography>
      </Card>
    );
  if (data.job && !data.needs_link && !data.needs_additional_scope)
    return (
      <Card variant="soft" color="success">
        <Typography level="title-lg">
          {data.change_order
            ? "Additional scope is ready for review"
            : "Your job is ready"}
        </Typography>
        <Typography>
          {data.change_order
            ? `A draft change order is linked to ${data.job.number}. Review it before applying any contract change.`
            : `Already linked to ${data.job.number}. No duplicate job will be created.`}
        </Typography>
        <Button
          component={Link}
          to={`/operations/jobs/${data.job.id}${data.change_order ? "?tab=change-orders" : ""}`}
          sx={{ alignSelf: "start" }}
        >
          Open job
        </Button>
        {materialsProposalId && !data.change_order && (
          <Button variant="outlined" onClick={() => setOrdering(true)}>
            Create materials order from accepted services
          </Button>
        )}
        {ordering && materialsProposalId && (
          <TakeoffOrderDialog
            proposalId={materialsProposalId}
            onClose={() => setOrdering(false)}
          />
        )}
      </Card>
    );
  return (
    <>
      <Card variant="soft" color="success">
        <Typography level="title-lg">
          {data.needs_additional_scope
            ? "Review additional accepted work"
            : data.needs_link
              ? "Link the existing job"
              : authorizedByStaff
                ? "Repair authorized — ready for a job"
                : "Proposal accepted — ready for a job"}
        </Typography>
        <Typography>
          {data.needs_additional_scope
            ? "This job already exists. Prepare a draft change order to review additional or replacement work; its current contract value stays intact."
            : data.needs_link
              ? "This opportunity already has a job. Link it here without creating another one."
              : data.can_confirm
                ? `Confirm once to mark the opportunity Won and create its linked job using the ${authorizedByStaff ? "authorized" : "accepted"} price.`
                : "An Org Admin has been notified to confirm job creation."}
        </Typography>
        {data.issue && <Alert color="warning">{data.issue}</Alert>}
        {data.can_confirm && (
          <Button
            disabled={!!data.issue}
            onClick={() => setOpen(true)}
            sx={{ alignSelf: "start" }}
          >
            {data.needs_additional_scope
              ? "Prepare change order for review"
              : data.needs_link
                ? "Link existing job"
                : "Mark won & create job"}
          </Button>
        )}
      </Card>
      <Modal
        open={showConfirmation && data.can_confirm && !data.issue}
        onClose={() => {
          if (!busy) close();
        }}
      >
        <ModalDialog
          sx={{
            width: "min(520px, calc(100vw - 32px))",
            maxHeight: "90dvh",
            overflowY: "auto",
          }}
        >
          <ModalClose disabled={busy} />
          <DialogTitle>
            {data.needs_additional_scope
              ? "Prepare additional scope for review?"
              : data.needs_link
                ? "Link this proposal to the existing job?"
                : "Turn this proposal into a job?"}
          </DialogTitle>
          <Typography>
            {data.needs_additional_scope
              ? "We’ll copy accepted selections into a draft change order. Review overlaps with existing work before issuing it. Nothing is sent automatically."
              : data.needs_link
                ? `This opportunity already has job ${data.job?.number}. We’ll link it without changing its price or creating another job.`
                : "We’ll mark the opportunity Won, transfer its records, and link the job automatically."}
          </Typography>
          <Card variant="soft">
            <Typography level="body-sm">
              {authorizedByStaff ? "Authorized" : "Accepted"} contract total
            </Typography>
            <Typography level="h3">
              {Number(data.total).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Typography>
          </Card>
          {!data.needs_link && !data.needs_additional_scope && (
            <FormControl>
              <FormLabel>Start the job in</FormLabel>
              <Select
                aria-label="Job board and status"
                value={destination ?? data.destination}
                onChange={(_, value) => setDestination(value)}
              >
                {data.destinations.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </FormControl>
          )}
          {!data.needs_link &&
            !data.needs_additional_scope &&
            !data.destinations.length && (
              <Alert color="warning">
                Add a job board and status in Settings before creating this job.
              </Alert>
            )}
          {error && <Alert color="danger">{error}</Alert>}
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button variant="plain" disabled={busy} onClick={close}>
              Not now
            </Button>
            <Button
              loading={busy}
              disabled={
                !data.needs_link &&
                !data.needs_additional_scope &&
                !(destination ?? data.destination)
              }
              onClick={() => void confirm()}
            >
              {data.needs_additional_scope
                ? "Prepare change order for review"
                : data.needs_link
                  ? "Link existing job"
                  : "Mark won & create job"}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
}
